import React, { useCallback, useMemo } from 'react';
import { Form, InputNumber, Select, Tooltip } from 'antd';
import { LineOutlined, InfoCircleOutlined } from '@ant-design/icons';

import {
  StyledFlowName,
  ThresholdTitle,
  ViewModeWrapper,
  ThresholdItem,
  StyledStartIcon,
  StyledPoor,
  StyledExcellent,
  StyledFair,
  FairLine,
  StyledSettingTitle,
  StyledSettingWrapper,
} from './styled';
import FlowThreshold from '@src/images/icon_flowSuccessRate.svg';
import { timeoutUnitOptions } from '@src/emp-3.0/constants/metrics';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import { checkSuccessConversionWindow, valueInSeconds } from '@src/emp-3.0/utils/metrics';
import WithoutEvent from './WithoutEvent';
import { useIsPatternMode } from '@src/pages/metric-templates/utils';
import PropogateDimension from './PropogateDimension/PropogateDimension';

interface ErrorMsgOfTimeCheck {
  timeout?: string;
  complete_timeout?: string;
  max?: string;
}
export const timeoutUnitMapping = timeoutUnitOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

export const FormSettingRow = ({
  label,
  children,
  required = false,
  tooltip,
  gap = 9,
}: {
  label: React.ReactNode;
  children?: React.ReactNode;
  required?: boolean;
  tooltip?: string;
  gap?: number;
}) => (
  <div
    style={{
      display: 'flex',
      gap: `${gap}px`,
      marginBottom: '8px',
      marginLeft: '28px',
    }}
  >
    <div style={{ display: 'flex', paddingTop: 5 }}>
      <StyledFlowName as="div">{label}</StyledFlowName>
      {required && <StyledStartIcon />}
    </div>
    {children}
    {tooltip && (
      <Tooltip title={tooltip}>
        <InfoCircleOutlined style={{ opacity: '0.4', fontSize: '18px' }} />
      </Tooltip>
    )}
  </div>
);

const FormThresholdItem = ({
  isView,
  field,
  dependency,
  validate,
}: {
  isView: boolean;
  field: string;
  dependency: string;
  validate: (value: number, dependencyValue: number) => Promise<any>;
}) => {
  const { status, enableRequired } = useFieldRequiredStatus([
    'definition',
    'settings',
    'threshold',
    field,
  ]);

  return (
    <ThresholdItem>
      <Form.Item
        name={['definition', 'settings', 'threshold', field]}
        dependencies={[['definition', 'settings', 'threshold', dependency]]}
        validateStatus={status}
        rules={
          [
            enableRequired && { required: true, message: 'Required' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const dependencyValue = getFieldValue([
                  'definition',
                  'settings',
                  'threshold',
                  dependency,
                ]);
                return validate(value, dependencyValue);
              },
            }),
          ].filter(Boolean) as React.ComponentProps<typeof Form.Item>['rules']
        }
        style={{ margin: 0 }}
      >
        {isView ? (
          <FormItemViewMode style={{ width: '58px' }} />
        ) : (
          <InputNumber style={{ width: '58px' }} min={0.01} max={99.99} step={1} />
        )}
      </Form.Item>
      <span style={{ alignSelf: 'baseline' }}>%</span>
    </ThresholdItem>
  );
};

export const FormItemViewMode: React.FC<{ value?: any; style?: any }> = ({ value, ...rest }) => {
  return <ViewModeWrapper {...rest}>{value}</ViewModeWrapper>;
};

export const TimeoutViewMode: React.FC<{ value?: any; style?: any }> = ({ value, ...rest }) => {
  const label = timeoutUnitMapping?.[value] ?? value;
  return <ViewModeWrapper {...rest}>{label}</ViewModeWrapper>;
};

const FlowSettings = ({ isView }) => {
  const form = Form.useFormInstance();
  const watchedTimeoutUnit = Form.useWatch(['definition', 'settings', 'timeout_unit']);
  const distributionMaxRange = useMemo(() => {
    if (watchedTimeoutUnit === 'seconds') {
      return 60 * 60; // 1 hour in seconds
    }
    if (watchedTimeoutUnit === 'minutes') {
      return 60; // 1 hour in minutes
    }
    return 0;
  }, [watchedTimeoutUnit]);

  const { status: timeoutStatus, enableRequired } = useFieldRequiredStatus([
    'definition',
    'settings',
    'timeout',
  ]);
  const { status: completeTimeoutStatus } = useFieldRequiredStatus([
    'definition',
    'settings',
    'complete_timeout',
  ]);
  const { status: durationMinStatus } = useFieldRequiredStatus([
    'definition',
    'settings',
    'distribution',
    'min',
  ]);
  const { status: durationMaxStatus } = useFieldRequiredStatus([
    'definition',
    'settings',
    'distribution',
    'max',
  ]);
  const startEvent = Form.useWatch(['definition', 'events', 'start_event'], form);
  const successEvent = Form.useWatch(['definition', 'events', 'success_event'], form);
  const eventIds = useMemo(() => {
    const ids: number[] = [];
    if (startEvent && typeof startEvent === 'number') {
      ids.push(startEvent);
    }
    if (successEvent && typeof successEvent === 'number') {
      ids.push(successEvent);
    }
    return ids;
  }, [startEvent, successEvent]);

  const { isPatternMode } = useIsPatternMode();
  /**
   * check time properties:
   * max value of timeout is one day
   * max value of complete_timeout is one day
   * complete_timeout value should be larger than timeout value and max value of distribution
   */
  const getErrorMsgOfTimeCheck = useCallback<() => Promise<ErrorMsgOfTimeCheck>>(async () => {
    const fieldsValue = form.getFieldsValue();
    const {
      timeout,
      timeout_unit,
      complete_timeout,
      complete_timeout_unit,
      distribution: { max } = {},
    } = fieldsValue?.definition?.settings ?? {};
    const timeoutValue = valueInSeconds({ value: timeout, unit: timeout_unit });
    const completeTimeoutValue = valueInSeconds({
      value: complete_timeout,
      unit: complete_timeout_unit,
    });
    const distMaxValue = valueInSeconds({ value: max, unit: timeout_unit });
    const result: ErrorMsgOfTimeCheck = {};

    if (timeoutValue !== null) {
      try {
        await checkSuccessConversionWindow(timeout_unit, timeout, 1);
      } catch (msg) {
        result.timeout = msg as string;
      }
    }
    if (completeTimeoutValue !== null) {
      try {
        await checkSuccessConversionWindow(complete_timeout_unit, complete_timeout, 1);
      } catch (msg) {
        result.complete_timeout = msg as string;
      }
      if (timeoutValue !== null && completeTimeoutValue < timeoutValue) {
        result.timeout =
          result.timeout ?? `Success limit should be less than or equal to the duration limit`;
        result.complete_timeout =
          result.complete_timeout ??
          `Duration limit should be greater than or equal to the success limit`;
      }
      if (distMaxValue !== null && completeTimeoutValue < distMaxValue) {
        result.max = `Duration distribution range should be less than or equal to the duration limit`;
        result.complete_timeout =
          result.complete_timeout ??
          `Duration limit should be greater than or equal to the duration distribution range`;
      }
    }
    return result;
  }, [form]);

  return (
    <>
      <FormSettingRow label="Flow success within" required={false}>
        <Tooltip title="Define the amount of time after the starting event during which flow success or error occurs">
          <InfoCircleOutlined
            style={{ opacity: '0.4', fontSize: '18px', alignSelf: 'baseline', paddingTop: 8 }}
          />
        </Tooltip>
        <StyledSettingWrapper>
          <Form.Item
            name={['definition', 'settings', 'timeout']}
            dependencies={[
              ['definition', 'settings', 'timeout_unit'],
              ['definition', 'settings', 'complete_timeout'],
              ['definition', 'settings', 'complete_timeout_unit'],
            ]}
            validateStatus={timeoutStatus}
            rules={
              [
                enableRequired && { required: true, message: 'Required' },
                {
                  async validator() {
                    const { timeout: errMsg } = await getErrorMsgOfTimeCheck();
                    if (errMsg) {
                      return Promise.reject(errMsg);
                    }
                  },
                },
              ].filter(Boolean) as React.ComponentProps<typeof Form.Item>['rules']
            }
            style={{ margin: 0 }}
          >
            {isView ? (
              <FormItemViewMode style={{ width: 80 }} />
            ) : (
              <InputNumber min={1} style={{ width: 80 }} />
            )}
          </Form.Item>
          <Form.Item
            name={['definition', 'settings', 'timeout_unit']}
            rules={[{ required: true }]}
            style={{ margin: 0 }}
          >
            {isView ? (
              <TimeoutViewMode style={{ width: '102px' }} />
            ) : (
              <Select options={timeoutUnitOptions} style={{ width: '102px' }} />
            )}
          </Form.Item>
        </StyledSettingWrapper>
      </FormSettingRow>
      <FormSettingRow label="Flow duration limit" required={false}>
        <Tooltip title="Define the duration of the flow">
          <InfoCircleOutlined
            style={{ opacity: '0.4', fontSize: '18px', alignSelf: 'baseline', paddingTop: 8 }}
          />
        </Tooltip>
        <StyledSettingWrapper>
          <Form.Item
            name={['definition', 'settings', 'complete_timeout']}
            dependencies={[
              ['definition', 'settings', 'complete_timeout_unit'],
              ['definition', 'settings', 'timeout'],
              ['definition', 'settings', 'timeout_unit'],
              ['definition', 'settings', 'distribution', 'max'],
            ]}
            validateStatus={completeTimeoutStatus}
            rules={
              [
                enableRequired && { required: true, message: 'Required' },
                {
                  async validator() {
                    const { complete_timeout: errMsg } = await getErrorMsgOfTimeCheck();
                    if (errMsg) {
                      return Promise.reject(errMsg);
                    }
                  },
                },
              ].filter(Boolean) as React.ComponentProps<typeof Form.Item>['rules']
            }
            style={{ margin: 0 }}
          >
            {isView ? (
              <FormItemViewMode style={{ width: 80 }} />
            ) : (
              <InputNumber min={1} style={{ width: 80 }} />
            )}
          </Form.Item>
          <Form.Item
            name={['definition', 'settings', 'complete_timeout_unit']}
            rules={[{ required: true }]}
            style={{ margin: 0 }}
          >
            {isView ? (
              <TimeoutViewMode style={{ width: '102px' }} />
            ) : (
              <Select options={timeoutUnitOptions} style={{ width: '102px' }} />
            )}
          </Form.Item>
        </StyledSettingWrapper>
      </FormSettingRow>
      <FormSettingRow
        gap={4}
        required={false}
        label="Aggregated Flow success rates"
        tooltip="Set the percentage thresholds for determining success in the flow"
      ></FormSettingRow>
      <div
        style={{
          display: 'flex',
          width: '85%',
          marginBottom: '10px',
          gap: 20,
        }}
      >
        <ThresholdTitle>
          <FlowThreshold />
        </ThresholdTitle>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip
            mouseEnterDelay={0.5}
            title="Enter the upper threshold boundary for poor flow performance"
          >
            <div style={{ display: 'flex', gap: '2px' }}>
              <StyledSettingTitle>
                <StyledPoor />
                <span>{'if <= '}</span>
              </StyledSettingTitle>
              <FormThresholdItem
                isView={isView}
                field="t1"
                dependency="t2"
                validate={(value, dependencyValue) => {
                  if (!value || !dependencyValue || value < dependencyValue) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Poor must be less than good'));
                }}
              />
            </div>
          </Tooltip>
          <div style={{ display: 'flex', alignItems: 'center', margin: '0px 10px' }}>
            <FairLine />
            <StyledFair />
            <FairLine />
          </div>
          <Tooltip
            mouseEnterDelay={0.5}
            title="Enter the target success threshold for the flow(eg: 95%)"
          >
            <div style={{ display: 'flex', gap: '2px' }}>
              <StyledSettingTitle>
                <StyledExcellent />
                <span>{'if >='}</span>
              </StyledSettingTitle>
              <FormThresholdItem
                isView={isView}
                field="t2"
                dependency="t1"
                validate={(value, dependencyValue) => {
                  return Promise.resolve();
                  // if (!value || value > dependencyValue) {
                  //   return Promise.resolve();
                  // }
                  // return Promise.reject(new Error('Good must be greater than poor'));
                }}
              />
            </div>
          </Tooltip>
        </div>
      </div>
      <FormSettingRow label="Flow duration distribution range">
        <Tooltip title="Define the expected value range of durations to be displayed on the distribution chart">
          <InfoCircleOutlined
            style={{ opacity: '0.4', fontSize: '18px', alignSelf: 'baseline', paddingTop: 8 }}
          />
        </Tooltip>
        <StyledSettingWrapper style={{ alignItems: 'center' }}>
          <Form.Item
            name={['definition', 'settings', 'distribution', 'min']}
            validateStatus={durationMinStatus}
            rules={enableRequired ? [{ required: true, message: 'Required' }] : []}
            style={{ margin: 0 }}
          >
            {isView ? (
              <FormItemViewMode style={{ width: '58px' }} />
            ) : (
              <InputNumber min={0} max={distributionMaxRange} style={{ width: '58px' }} />
            )}
          </Form.Item>
          <LineOutlined style={{ fontSize: '5px' }} />
          <Form.Item
            name={['definition', 'settings', 'distribution', 'max']}
            dependencies={[
              ['definition', 'settings', 'complete_timeout'],
              ['definition', 'settings', 'complete_timeout_unit'],
            ]}
            validateStatus={durationMaxStatus}
            rules={
              [
                enableRequired && { required: true, message: 'Required' },
                {
                  async validator() {
                    const { max: errMsg } = await getErrorMsgOfTimeCheck();
                    if (errMsg) {
                      return Promise.reject(errMsg);
                    }
                  },
                },
              ].filter(Boolean) as React.ComponentProps<typeof Form.Item>['rules']
            }
            style={{ margin: 0 }}
          >
            {isView ? (
              <FormItemViewMode style={{ width: '58px' }} />
            ) : (
              <InputNumber min={0} max={distributionMaxRange} style={{ width: '58px' }} />
            )}
          </Form.Item>

          <div style={{ color: 'rgba(0, 0, 0, 0.65)' }}>
            {timeoutUnitOptions.find((item) => item.value === watchedTimeoutUnit)?.label}
          </div>
        </StyledSettingWrapper>
      </FormSettingRow>
      {!isPatternMode && <WithoutEvent form={form} isView={isView} />}
      {!isPatternMode && (
        <div style={{ marginLeft: '28px' }}>
          <PropogateDimension form={form} isView={isView} eventIds={eventIds} />
        </div>
      )}
    </>
  );
};

export default FlowSettings;
